import { Slot } from '@radix-ui/react-slot'
import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

/**
 * Button — copied from the dashboard's local button
 * (apps/schedugram-client/react-src/components/ui/button.tsx, the
 * style-guide source of truth) and trimmed to the variants external-review
 * needs. Keep classes in sync with the client when porting new variants.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-inter text-sm font-medium transition-all duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-98 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-blue-500 text-white shadow hover:bg-blue-600 active:bg-blue-700',
        'blue-primary':
          'bg-blue-500 text-white shadow hover:bg-blue-600 active:bg-blue-700 disabled:bg-blue-200',
        'blue-secondary':
          'bg-blue-50 text-blue-600 shadow-sm hover:bg-blue-100 border border-blue-100',
        'red-primary':
          'bg-red-500 text-white shadow hover:bg-red-600 active:bg-red-700 disabled:bg-red-200',
        'red-secondary':
          'border border-red-100 bg-transparent text-red-600 shadow-sm hover:bg-red-25 active:bg-red-50 disabled:bg-transparent disabled:text-neutral-800 disabled:border-neutral-100',
        'green-primary':
          'bg-green-500 text-white shadow hover:bg-green-600 active:bg-green-700 disabled:bg-green-200',
        'green-secondary':
          'border border-green-100 bg-transparent text-green-600 shadow-sm hover:bg-green-25 active:bg-green-50 disabled:bg-transparent disabled:text-neutral-800 disabled:border-neutral-100',
        outline:
          'border border-neutral-100 bg-white shadow-sm hover:bg-blue-25 hover:text-blue-500 hover:border-neutral-100 active:bg-blue-50 active:text-blue-500 active:border-neutral-100 data-[state=open]:bg-blue-50 data-[state=open]:text-blue-500 data-[state=open]:border-neutral-100',
        ghost:
          'hover:bg-blue-25 hover:text-blue-500 active:bg-blue-50 active:text-blue-500 disabled:bg-transparent',
        link: 'text-blue-500 underline-offset-4 hover:underline hover:text-blue-600 active:text-blue-700 disabled:bg-transparent',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 py-1.5',
        md: 'h-9 px-4 py-2',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
